import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager, { nillToNull } from '../../managers/network/DataQueryManager'

export enum RequestObjectType {
  Usuario = 'usuario',
  Armador = 'armador',
  Tripulante = 'tripulante',
  Embarcacion = 'embarcacion',
  Proveedor = 'proveedor',
  Representante = 'representante',
  Asociacion_embarcacion = 'asociacion_embarcacion', // Se envia junto a la embarcacion para asociar armador y embarcacion
  // id_objeto: EmbarcacionID
  // id_objeto_2: ArmadorID
  Asociacion_tripulante = 'asociacion_tripulante', // Se envia junto al tripulante para asociar armador y tripulante
  // id_objeto: TripulanteID
  // id_objeto_2: ArmadorID
  Modificacion = 'modificacion',

  // Tipos temporales para procesos de viaje
  Preacreditacion = 'preacreditacion', // Para asociar un tripulante o armador a un viaje
  // id_objeto: ViajeID
  // tipo_objeto_2: 'armador' | 'tripulante'
  // id_objeto_2: ArmadorID | TripulanteID
  PatronViaje = 'patron_viaje', // Para asociar un tripulante (patron) a un viaje
  // id_objeto: ViajeID
  // id_objeto_2: TripulanteID
  PosicionamientoViaje = 'posicionamiento_viaje', // Para marcar el posicionamiento de un viaje
  // id_objeto: ViajeID
  PermisoMuelleWeste = 'permiso_muelle_weste', // Para permisos de muelle weste
  // id_objeto: ViajeID

  Servicio = 'servicio', // Para solicitud de servicios
  VehiculoServicio = 'vehiculo_servicio', // Para solicitud de servicios de vehiculos
  // id_objeto: ServicioID
  // id_objeto_2: VehiculoServicioID
  TarifaEspecial = 'tarifa_especial', // Para solicitud de tarifas especiales
  // id_objeto: ServicioID
  // id_objeto_2: TarifaEspecialID
  PosicionamientoServicio = 'posicionamiento_servicio', // Para marcar el posicionamiento de un servicio
  // id_objeto: ServicioID

  Cobro = 'cobro', // Para solicitud de cobros
  // estado: Estado de pago del cobro (PENDIENTE, APROBADO, RECHAZADO)
  // id_objeto: CobroID
  // tipo_objeto_2: 'viaje' | 'servicio'
  // id_objeto_2: ViajeID | ServicioID

  RepresentanteArmador = 'representante_armador', // Para solicitud de representante de armador
  // id_objeto: ArmadorID. Id del armador al que se le asignara el representante
  // id_objeto_2: UserID. Id del usuario que se convertira en representante
  RepresentanteProveedor = 'representante_proveedor', // Para solicitud de representante de proveedor
  // id_objeto: ProveedorID. Id del proveedor al que se le asignara el representante
  // id_objeto_2: UserID. Id del usuario que se convertira en representante

  RolAlternativo = 'rol_alternativo', // Para asociar un usuario interino a un rol alternativo dinámico
  // id_objeto: UserID (id_usuario del interino)
  // id_objeto_2: RolAlternativoID

  Esporadico = 'esporadico', // Acreditación temporal de un usuario esporádico (rol=7) — RN36-RN42
  // id_objeto: UserID (id_usuario del esporádico)

  AutorizacionOperacion = 'autorizacion_operacion', // Solicitud de Autorización de Operación (AO) - Etapa 1
  // id_objeto: EmbarcacionID
  // id_objeto_2: ArmadorID
  // Dos sub-roles según presencia de id_objeto_2:
  //   · SOLICITUD BASE ("validez como persona"): id_objeto_2=NULL, fecha_caducidad=NULL o lejana.
  //     Se crea automáticamente al inscribir por primera vez. Se aprueba sin intervención.
  //     Marca que el RUT está enrolado como esporádico.
  //   · SOLICITUD DE TRABAJO: id_objeto_2 = RolAlternativoID (FK rol_alternativo.id).
  //     tipo_objeto_2 = NULL. fecha_caducidad = fin visita.
  //     El cargo se hereda del rol_alternativo (con su horario, logica_credencial, etc.).
  //     Cada visita es una nueva solicitud de trabajo (NO se renuevan).
  //     Re-arquitectura 2026-05-18: el cargo vuelve a ser FK al catálogo rol_alternativo
  //     compartido con INTERINO (se descarta el texto libre del 2026-05-14).

  AsociacionEsporadico = 'asociacion_esporadico', // Vínculo esporádico ↔ armador y/o embarcación (RN39)
  // id_objeto:   UserID (id_usuario del esporádico)
  // tipo_objeto_2: 'armador' | 'embarcacion' — discrimina contra qué entidad se asocia
  // id_objeto_2:   ArmadorID  cuando tipo_objeto_2='armador'
  //                EmbarcacionID cuando tipo_objeto_2='embarcacion'
  // fecha_caducidad: igual que la solicitud de trabajo asociada
  // Por cada solicitud de trabajo se crean 1 o 2 filas de asociación (al menos
  // una entre armador / embarcación; pueden coexistir ambas).
}

export enum RequestStatus {
  PENDING = 0,
  APPROVED = 1,
  REJECTED = 2,
  INCOMPLETE = 3,
  // APPROVED_LEGAL solo aplica a solicitudes RepresentanteArmador: marca al rep como
  // Representante Legal (capacidades equivalentes al armador, sin matriz por embarcación).
  // Para todo lo demás, una solicitud con estado=4 se trata como aprobada (usar isApproved).
  APPROVED_LEGAL = 4,
}

/**
 * True si la solicitud está aprobada en cualquiera de sus variantes
 * (APPROVED clásico o APPROVED_LEGAL para representantes). Usar este helper
 * en sitios que solo necesitan saber "está aprobada"; usar comparación
 * estricta con APPROVED_LEGAL en lugares que necesitan tratar Legal aparte
 * (PermisosResolver, UI de aprobación de representantes).
 */
export function isApproved(estado: RequestStatus | number | undefined | null): boolean {
  return estado === RequestStatus.APPROVED || estado === RequestStatus.APPROVED_LEGAL
}

export interface IRequest {
  id: number
  creador: number
  revisor: number | null
  estado: RequestStatus
  fecha_solicitud: string
  fecha_respuesta: string | null
  fecha_caducidad: string
  tipo_objeto: string
  id_objeto: string
  tipo_objeto_2: string | null // Para futuras expansiones
  id_objeto_2: string | null // Para futuras expansiones
  razon_rechazo: string
  // Inicio de vigencia explícito (nullable). Si no se envía, el backend lo deja NULL
  // (= se asume fecha_solicitud). En el INSERT se castea con NULLIF(?,'')::timestamp y
  // en el UPDATE con COALESCE(...) para preservar el valor si no se manda. Solo lo usan
  // hoy las solicitudes con vigencia (esporádico, rol_alternativo, etc.).
  fecha_inicio_vigencia?: string | null
  // Marca SOLO de frontend: la solicitud fue generada al vuelo (no existe en el backend).
  // Se usa para el patrón "solicitud siempre visible": cuando una persona está completa pero
  // nunca se generó su solicitud (p. ej. un patrón que reutiliza el usuario del armador), el hub
  // sintetiza una solicitud PENDING virtual para que el administrador SIEMPRE la vea. Al resolverla
  // (aprobar/rechazar) se materializa en el backend (ver RequestShower.saveChanges). Nunca se envía
  // a la API: las operaciones insert/update mapean los campos explícitamente e ignoran esta marca.
  esVirtual?: boolean
}

export interface IRequested<T> {
  data: T
  requestData: IRequest | null
}

export default class RequestsServices {
  private bucketID: string | null = null

  constructor() {
    // Registrar el bucket para solicitudes
    this.bucketID = dataStorageManager.registerBucket<IRequest>(
      this.getAllSolicitudes.bind(this), // Asegúrate de enlazar el contexto correcto
      {
        refreshRatio: 5 * 60 * 1000, // Refrescar cada 5 minutos
        //preload: true, // Preload
        bucketID: BucketDefaultIDs.SOLICITUDES, // ID del bucket
        operations: new Map<string, DMS_Operation>([
          [
            'insertRequest',
            {
              operation: this.insertRequest.bind(this),
              reloadAfter: true,
            },
          ],
          [
            'updateRequest',
            {
              operation: this.updateRequest.bind(this),
              reloadAfter: true,
            },
          ],
        ]), // Las operaciones se añadiran despues
      },
    )
  }

  startup() {}

  async getAllSolicitudes(): Promise<IRequest[]> {
    try {
      const response = await dataQueryManager.addQuery<IRequest | IRequest[]>({
        query: `${import.meta.env.VITE_BASE_URL}/bitacora/1/op_select_solicitud`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
      })

      if (response.data && Array.isArray(response.data)) {
        const cleanData: IRequest[] = response.data.map((solicitud) => {
          return {
            id: solicitud.id,
            creador: solicitud.creador,
            revisor: typeof solicitud.revisor == 'object' ? null : solicitud.revisor,
            estado: solicitud.estado,
            fecha_solicitud: solicitud.fecha_solicitud,
            fecha_respuesta:
              typeof solicitud.fecha_respuesta == 'object' ? null : nillToNull(solicitud.fecha_respuesta.toString()),
            fecha_caducidad: solicitud.fecha_caducidad,
            tipo_objeto: solicitud.tipo_objeto,
            id_objeto: solicitud.id_objeto,
            tipo_objeto_2:
              typeof solicitud.tipo_objeto_2 == 'object' ? null : nillToNull(solicitud.tipo_objeto_2!.toString()),
            id_objeto_2:
              typeof solicitud.id_objeto_2 == 'object' ? null : nillToNull(solicitud.id_objeto_2!.toString()),
            // El backend devuelve xsi:nil como objeto { "@nil": "true" }; lo normalizamos a ''
            razon_rechazo:
              typeof solicitud.razon_rechazo == 'object' ? '' : nillToNull(solicitud.razon_rechazo!.toString()) ?? '',
            fecha_inicio_vigencia:
              typeof solicitud.fecha_inicio_vigencia == 'object'
                ? null
                : nillToNull(solicitud.fecha_inicio_vigencia!.toString()),
          }
        })
        return cleanData
      } else if (response.data) {
        const cleanData = {
          id: response.data.id,
          creador: response.data.creador,
          revisor: typeof response.data.revisor == 'object' ? null : response.data.revisor,
          estado: response.data.estado,
          fecha_solicitud: response.data.fecha_solicitud,
          fecha_respuesta: typeof response.data.fecha_respuesta == 'object' ? null : response.data.fecha_respuesta,
          fecha_caducidad: response.data.fecha_caducidad,
          tipo_objeto: response.data.tipo_objeto,
          id_objeto: response.data.id_objeto,
          tipo_objeto_2:
            typeof response.data.tipo_objeto_2 == 'object' ? null : nillToNull(response.data.tipo_objeto_2!.toString()),
          id_objeto_2:
            typeof response.data.id_objeto_2 == 'object' ? null : nillToNull(response.data.id_objeto_2!.toString()),
          razon_rechazo:
            typeof response.data.razon_rechazo == 'object'
              ? ''
              : nillToNull(response.data.razon_rechazo!.toString()) ?? '',
          fecha_inicio_vigencia:
            typeof response.data.fecha_inicio_vigencia == 'object'
              ? null
              : nillToNull(response.data.fecha_inicio_vigencia!.toString()),
        }
        return [cleanData]
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    }
    return []
  }

  async insertRequest(
    requestData: Omit<IRequest, 'id' | 'fecha_solicitud' | 'fecha_respuesta' | 'revisor' | 'razon_rechazo'>,
  ): Promise<boolean> {
    try {
      const op_insert_solicitud: Record<string, unknown> = {
        creador: requestData.creador,
        estado: requestData.estado,
        tipo_objeto: requestData.tipo_objeto,
        fecha_caducidad: requestData.fecha_caducidad,
        id_objeto: requestData.id_objeto,
        tipo_objeto_2: requestData.tipo_objeto_2,
        id_objeto_2: requestData.id_objeto_2,
      }
      // Solo lo enviamos cuando hay valor; si se omite, el backend lo deja NULL (NULLIF).
      if (requestData.fecha_inicio_vigencia) {
        op_insert_solicitud.fecha_inicio_vigencia = requestData.fecha_inicio_vigencia
      }
      const body = { op_insert_solicitud }
      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/bitacora/1/op_insert_solicitud`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })
      if (response && (response.status === 200 || response.data !== undefined)) {
        return true
      }
    } catch (error) {
      console.error('Error inserting solicitud:', error)
    }
    return false
  }

  async updateRequest(requestData: Omit<IRequest, 'fecha_respuesta'>): Promise<boolean> {
    try {
      const op_update_solicitud: Record<string, unknown> = {
        revisor: requestData.revisor,
        estado: requestData.estado,
        id: requestData.id,
        fecha_caducidad: requestData.fecha_caducidad,
        razon_rechazo: requestData.razon_rechazo,
      }
      // Si no se envía, el backend preserva el valor existente (COALESCE en el UPDATE).
      if (requestData.fecha_inicio_vigencia) {
        op_update_solicitud.fecha_inicio_vigencia = requestData.fecha_inicio_vigencia
      }
      const body = { op_update_solicitud }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/bitacora/1/op_update_solicitud`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })
      if (response && (response.status === 200 || response.data !== undefined)) {
        return true
      }
    } catch (error) {
      console.error('Error updating user:', error)
    }
    return false
  }
}
